import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { convertLength, convertMass, convertToDecimal, PAGE, Patient, ToastService, truncDecimal } from '../../core';
import { IPatientControl, PatientControl, patientErrorMsgs, PatientForm, patientIdNotUnique, setPatientValidators } from '../../models';
import { PatientService, UserService } from '../../services';
import { BaseComponent, FormValidationMsgDirective } from '../../shared';


/**
* Patient form component to create/edit patient data.
*/
@Component({
	selector: 'patient-form',
	templateUrl: './patient-form.component.html',
	imports: [
		FormsModule,
		NgbCollapse,
		ReactiveFormsModule,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PatientFormComponent extends BaseComponent implements OnInit, OnDestroy {

	public route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly userSrv = inject(UserService);
	private readonly toastSrv = inject(ToastService);
	private readonly patientSrv = inject(PatientService);

	readonly patientForm = new FormGroup<IPatientControl>(new PatientControl());
	readonly errorMsgs = patientErrorMsgs;
	patient: Patient | null = null;
	patientGuid: string | null = null;

	isExtraDataVisible = false;
	patientBMI: number | null = null;

	private isSaving = false;
	private isAnotherUser = false;
	private _usrSub$?: Subscription;


	ngOnInit(): void {
		setPatientValidators(this.patientForm);
		this.initPatientForm();
	}

	private initPatientForm(): void {
		this.patientGuid = this.route.snapshot.queryParamMap.get('patientGuid');
		if (this.patientGuid == null) {
			this.patientForm.reset();
		} else {
			this.patientSrv.getPatient(this.patientGuid).pipe(
				tap(pat => this.patient = pat),
				map(pat => this.patientSrv.createPatientForm(pat))
			).subscribe(patForm => {
				this.patientForm.patchValue(patForm);
				this.convertKgToLb();
				this.convertCmToFt();
				this.patientForm.markAsUntouched();
				this._usrSub$ = this.userSrv.isCustomerCareOtherUser(this.patient?.userGuid ?? null).subscribe(res => this.isAnotherUser = res);
			});
		}
	}

	get isSaveDisabled(): boolean {
		return !this.patientForm.valid || this.isSaving || this.isAnotherUser;
	}

	get isFormDirty(): boolean {
		return this.patientForm.dirty;
	}

	/**
	* Trim patient id input field.
	*/
	trimPatientId(): void {
		if (this.patientForm.value.patientId) {
			this.patientForm.controls.patientId.setValue(this.patientForm.value.patientId.trim());
		}
	}

	/**
	* Trim patient intitials input field.
	*/
	trimPatientInitials(): void {
		if (this.patientForm.value.patientInitials) {
			this.patientForm.controls.patientInitials.setValue(this.patientForm.value.patientInitials.trim());
		}
	}

	/**
	* Update age when click infant age checkox.
	*/
	updateAge(): void {
		this.patientForm.value.patientInfant ? this.patientForm.controls.patientAge.setValue(0) : this.patientForm.controls.patientAge.setValue(null);
		this.patientForm.controls.patientAge.markAsTouched();
	}

	/**
	* Handle weight in kg and convert it in lb.
	*/
	convertKgToLb(): void {
		const val = truncDecimal(this.patientForm.value.patientWeightKg ?? null);
		this.patientForm.controls.patientWeightKg.setValue(val);
		const kg = convertToDecimal(this.patientForm.value.patientWeightKg ?? null);
		this.patientForm.controls.patientWeightLb.setValue(convertMass(kg, 'kg')?.toString() ?? null);
		this.patientForm.controls.patientWeightLb.markAsTouched();
		this.calculateBMI();
	}

	/**
	* Handle weight in lb and convert it in kg.
	*/
	convertLbToKg(): void {
		const val = truncDecimal(this.patientForm.value.patientWeightLb ?? null);
		this.patientForm.controls.patientWeightLb.setValue(val);
		const lb = convertToDecimal(this.patientForm.value.patientWeightLb ?? null);
		this.patientForm.controls.patientWeightKg.setValue(convertMass(lb, 'lb')?.toString() ?? null);
		this.patientForm.controls.patientWeightKg.markAsTouched();
		this.calculateBMI();
	}

	/**
	* Handle height in cm and convert it in ft.
	*/
	convertCmToFt(): void {
		const val = truncDecimal(this.patientForm.value.patientHeightCm ?? null);
		this.patientForm.controls.patientHeightCm.setValue(val);
		const cm = convertToDecimal(this.patientForm.value.patientHeightCm ?? null);
		this.patientForm.controls.patientHeightFt.setValue(convertLength(cm, 'cm')?.toString() ?? null);
		this.patientForm.controls.patientHeightFt.markAsTouched();
		this.calculateBMI();
	}

	/**
	* Handle height in ft and convert it in cm.
	*/
	convertFtToCm(): void {
		const val = truncDecimal(this.patientForm.value.patientHeightFt ?? null);
		this.patientForm.controls.patientHeightFt.setValue(val);
		const ft = convertToDecimal(this.patientForm.value.patientHeightFt ?? null);
		this.patientForm.controls.patientHeightCm.setValue(convertLength(ft, 'ft')?.toString() ?? null);
		this.patientForm.controls.patientHeightCm.markAsTouched();
		this.calculateBMI();
	}

	private calculateBMI(): void {
		const height = this.patientForm.value.patientHeightCm;
		const weight = this.patientForm.value.patientWeightKg;
		if (height && weight && +height && +weight && !Number.isNaN(Number.parseFloat(height)) && !Number.isNaN(Number.parseFloat(weight))) {
			this.patientBMI = Math.round((Number.parseFloat(weight) / Math.pow(Number.parseFloat(height) / 100, 2) * 10)) / 10;
		} else {
			this.patientBMI = null;
		}
	}

	/**
	* Handle save and edit patient.
	*/
	savePatient(createPlan: boolean): void {
		const patForm = this.patientForm.value as PatientForm;
		if (this.patientGuid == null) {
			this.isSaving = true;
			this.patientSrv.savePatientForm(patForm).pipe(
				finalize(() => this.isSaving = false)
			).subscribe({
				next: (patient: Patient) => {
					this.toastSrv.showSuccess(this.labels().PATIENT_CREATION_COMPONENT_SAVE_MESSAGE);
					this.patient = patient;
					this.patientGuid = this.patient.id;
					if (createPlan)
						this.navigateToPlanForm(this.patientGuid);
					else
						this.navigateToPatientList();
				},
				error: (err: Error) => {
					this.toastSrv.showError(this.labels().PATIENT_CREATION_COMPONENT_ERROR_MESSAGE);
					this.handlePatientIdNotUnique(err);
				}
			});
		} else {
			this.isSaving = true;
			this.patientSrv.updatePatient(this.patient!, patForm).pipe(
				finalize(() => this.isSaving = false)
			).subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PATIENT_CREATION_COMPONENT_UPDATE_MESSAGE);
					if (createPlan)
						this.navigateToPlanForm(this.patientGuid!);
					else
						this.navigateToPatientList();
				},
				error: (err: Error) => {
					this.toastSrv.showError(this.labels().PATIENT_CREATION_COMPONENT_ERROR_MESSAGE);
					this.handlePatientIdNotUnique(err);
				}
			});
		}
	}

	private navigateToPatientList(): void {
		this.router.navigateByUrl(`/${PAGE.LIST}`);
	}
	private navigateToPlanForm(patGuid: string): void {
		this.router.navigate([`/${PAGE.PLAN_NEW}`], { queryParams: { patientGuid: patGuid } });
	}

	private handlePatientIdNotUnique(err: Error): void {
		if (err.message == 'PatientNumberNotUnique') {
			this.patientForm.controls.patientId.setErrors({ [`${patientIdNotUnique}`]: null });
		}
	}


	ngOnDestroy(): void {
		this._usrSub$?.unsubscribe();
	}

}
